/**
 * Crop-compositing helper for the update-photo dialog. Decoupled from `react-easy-crop`'s own
 * types on purpose — this file only knows about a plain pixel rect, so it stays testable and
 * swappable independent of the crop UI library.
 *
 * `react-easy-crop`'s `onCropComplete` already hands back the crop region in source-image pixel
 * coordinates, so there's no devicePixelRatio/display-scale math to redo here (legacy's
 * hand-rolled cropper had to do that itself). The output is always normalized to a fixed
 * `outputSize × outputSize` square, regardless of how large the user's drag-box or source photo
 * was — avoiding legacy's variable, drag-box-dependent output dimensions. The circular
 * presentation is CSS (`rounded-full` on `AvatarImage`), not a pixel-level clip here, matching
 * both legacy and the shared Avatar primitive.
 */
export interface CroppedAreaPixels {
  x: number;
  y: number;
  width: number;
  height: number;
}

export async function compositeCroppedPhoto(
  image: HTMLImageElement,
  croppedAreaPixels: CroppedAreaPixels,
  outputSize = 512,
): Promise<Blob> {
  const canvas = document.createElement('canvas');
  canvas.width = outputSize;
  canvas.height = outputSize;

  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Canvas 2D context is unavailable');
  }

  ctx.drawImage(
    image,
    croppedAreaPixels.x,
    croppedAreaPixels.y,
    croppedAreaPixels.width,
    croppedAreaPixels.height,
    0,
    0,
    outputSize,
    outputSize,
  );

  return new Promise((resolve, reject) => {
    // No quality argument: PNG is lossless, so the third `toBlob` param (which only affects
    // lossy formats like image/jpeg or image/webp) would be silently ignored anyway.
    canvas.toBlob((blob) => {
      if (blob) {
        resolve(blob);
      } else {
        reject(new Error('Failed to encode cropped photo'));
      }
    }, 'image/png');
  });
}
